import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/veteran-facing-services-tools/veteran-facing-services-tools/packages/documentation/src/layouts/SidebarLayout.jsx";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "run-and-build-vagov"
    }}>{`Run and build VA.gov`}</h1>
    <div className="deprecation-message">
  <h2>We're moving our docs!</h2>
  <h3>
    Find{" "}
    <a href="https://depo-platform-documentation.scrollhelp.site/developer-docs/Running-VA.gov-locally.1844248620.html">
      the latest version of this page
    </a>{" "}
    on the Platform website.
  </h3>
  <h3>
    Still can't find what you're looking for? Reach out to{" "}
    <a href="https://dsva.slack.com/archives/CBU0KDSB1">
      #vfs-platform-support
    </a>{" "}
    on Slack.
  </h3>
    </div>
    <h2 {...{
      "id": "prerequisite"
    }}>{`Prerequisite`}</h2>
    <ul>
      <li parentName="ul">{`Complete the `}<a parentName="li" {...{
          "href": "/veteran-facing-services-tools/getting-started"
        }}>{`first`}</a>{` step in `}<em parentName="li">{`Start up guide`}</em>{`.`}</li>
    </ul>
    <h2 {...{
      "id": "locally-run-vagov"
    }}>{`Locally run VA.gov`}</h2>
    <p>{`Start the watch task:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`yarn watch
`}</code></pre>
    <h3 {...{
      "id": "build"
    }}>{`Build`}</h3>
    <h4 {...{
      "id": "vets-website"
    }}>{`vets-website`}</h4>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Webpack`}</strong>{` manages this build pipeline. At a high level, this process:`}
        <ol parentName="li">
          <li parentName="ol">{`Starts `}<strong parentName="li">{`Webpack`}</strong>{`'s devevelopment server which`}
            <ul parentName="li">
              <li parentName="ul"><strong parentName="li">{`Builds`}</strong>{` the `}<strong parentName="li">{`JavaScript`}</strong>{` and `}<strong parentName="li">{`CSS`}</strong>{` bundles`}</li>
              <li parentName="ul"><strong parentName="li">{`Serves`}</strong>{` the `}<strong parentName="li">{`JavaScript`}</strong>{` and `}<strong parentName="li">{`CSS`}</strong>{` bundles built by Webpack as well as any scaffolded application pages `}<a parentName="li" {...{
                  "href": "http://localhost:3001"
                }}>{`http://localhost:3001`}</a></li>
            </ul>
          </li>
        </ol>
      </li>
    </ul>
    <h4 {...{
      "id": "content-build"
    }}>{`content-build`}</h4>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Metalsmith`}</strong>{` manages this build pipeline. At a high level, this process:`}
        <ol parentName="li">
          <li parentName="ol">{`Retrieves and `}<strong parentName="li">{`builds`}</strong>{` all of the `}<strong parentName="li">{`static pages`}</strong>{` sourced from `}<inlineCode parentName="li">{`vagov-content`}</inlineCode>{` or Drupal`}</li>
          <li parentName="ol"><strong parentName="li">{`Serves`}</strong>{` the `}<strong parentName="li">{`static content`}</strong>{` built by Metalsmith at `}<a parentName="li" {...{
              "href": "http://localhost:3002"
            }}>{`http://localhost:3002`}</a></li>
        </ol>
      </li>
    </ul>
    <h3 {...{
      "id": "output"
    }}>{`Output`}</h3>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Metalsmith`}</strong>{` in the `}<inlineCode parentName="li">{`content-build`}</inlineCode>{` repository outputs `}<strong parentName="li">{`static pages`}</strong>{` to`}<inlineCode parentName="li">{`build/localhost`}</inlineCode></li>
      <li parentName="ul"><strong parentName="li">{`Webpack`}</strong>{` in the `}<inlineCode parentName="li">{`vets-website`}</inlineCode>{` repository development server has `}<strong parentName="li">{`no output`}</strong>{`. `}<strong parentName="li">{`JavaScript`}</strong>{` and `}<strong parentName="li">{`CSS`}</strong>{` are kept on disk.`}</li>
    </ul>
    <h3 {...{
      "id": "watch-and-rebuild"
    }}>{`Watch and rebuild`}</h3>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Metalsmith`}</strong>{` in the `}<inlineCode parentName="li">{`content-build`}</inlineCode>{` repository will rebuild `}<strong parentName="li">{`static pages`}</strong>{` when changes are saved to `}<strong parentName="li">{`templates`}</strong>{` or content in `}<inlineCode parentName="li">{`vagov-content`}</inlineCode></li>
      <li parentName="ul"><strong parentName="li">{`Webpack`}</strong>{` in the `}<inlineCode parentName="li">{`vets-website`}</inlineCode>{` repository will rebuild when changes are saved to `}<strong parentName="li">{`JavaScript`}</strong>{` or `}<strong parentName="li">{`SCSS`}</strong></li>
      <li parentName="ul">{`Both updates require a browser refresh to see changes.`}</li>
      <li parentName="ul">{`Changes to build process or configuration files are typically not picked up, and require a restart of the watch task to become active.`}</li>
    </ul>
    <h2 {...{
      "id": "locally-build-and-run-for-specific-environment"
    }}>{`Locally build and run for specific environment`}</h2>
    <p><em parentName="p">{`Note: most of the time, it's better to use the `}<inlineCode parentName="em">{`watch`}</inlineCode>{` task to build the site locally since it is the most developer-friendly experience.`}</em></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Environment`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Build Command`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Output directory`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Run Command`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`localhost`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`yarn build`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`build/localhost`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`npx http-server -p 3001 build/localhost`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`dev.va.gov`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`yarn build --buildtype=vagovdev`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`build/vagovdev`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`npx http-server -p 3001 build/vagovdev`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`staging.va.gov`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`NODE_ENV=production yarn build --buildtype=vagovstaging`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`build/vagovstaging`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`npx http-server -p 3001 build/vagovstaging`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "http://www.va.gov"
            }}>{`www.va.gov`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`NODE_ENV=production yarn build --buildtype=vagovprod`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`build/vagovprod`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`npx http-server -p 3001 build/vagovprod`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "build-commands"
    }}>{`Build commands`}</h3>
    <ul>
      <li parentName="ul"><strong parentName="li">{`create`}</strong>{` the `}<strong parentName="li">{`static pages`}</strong>{` from the markdown content in the `}<inlineCode parentName="li">{`vagov-content`}</inlineCode>{` repository and data queried from the Drupal API into their `}<strong parentName="li">{`output directory`}</strong></li>
      <li parentName="ul"><strong parentName="li">{`create`}</strong>{` the `}<strong parentName="li">{`JavaScript`}</strong>{` and `}<strong parentName="li">{`CSS`}</strong>{` files with Webpack into `}<inlineCode parentName="li">{`/generated`}</inlineCode>{` folder in their `}<strong parentName="li">{`output directory`}</strong>{` with Webpack`}</li>
    </ul>
    <p><em parentName="p">{`Production like builds are required for staging and production environments. `}<strong parentName="em">{`NODE_ENV=production`}</strong>{` environment variable is required to be set when running these build commands`}</em></p>
    <h3 {...{
      "id": "run-commands"
    }}>{`Run commands`}</h3>
    <ul>
      <li parentName="ul"><strong parentName="li">{`start`}</strong>{` an `}<inlineCode parentName="li">{`http-server`}</inlineCode>{` that `}<strong parentName="li">{`serves`}</strong>{` the `}<strong parentName="li">{`static pages`}</strong>{` from the `}<strong parentName="li">{`output directory`}</strong>{` at `}<a parentName="li" {...{
          "href": "https://localhost:3001"
        }}>{`http://localhost:3001`}</a></li>
    </ul>
    <p><em parentName="p">{`Typically, the reason for building the site locally like this is to build it in production mode and check that it is behaving as you'd expect.`}</em></p>
    <p><em parentName="p"><strong parentName="em">{`Deep-linking`}</strong>{` to urls that are rendered by `}<strong parentName="em">{`React`}</strong>{` apps on VA.gov `}<strong parentName="em">{`will not work`}</strong>{` when you run the site this way, as that relies on some server-side routing that is handled in nginx (or the Webpack dev server when running the `}<inlineCode parentName="em">{`watch`}</inlineCode>{` task)`}</em></p>
    <h2 {...{
      "id": "related-source"
    }}>{`Related source`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/content-build/tree/main/src/site/stages/build"
        }}>{`Metalsmith build script`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/department-of-veterans-affairs/vets-website/blob/main/config/webpack.dev.config.js"
        }}>{`Webpack development server config`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      